// Okendo custom reviews — fetches reviews + aggregate for a product via the
// Okendo Storefront REST API and renders them into each `[data-oke-reviews]`
// container. Product page only (no product id => nothing rendered).
// Docs: https://docs.okendo.io/on-site/storefront-rest-api

(function () {
  var API_ORIGIN = 'https://api.okendo.io';
  var INITIAL_COUNT = 4; // reviews shown on first load
  var MORE_COUNT = 10; // reviews revealed per "Show More" click
  var FETCH_LIMIT = 100; // reviews fetched up front in a single request

  var STAR_PATH =
    'M10.4552 0.846855C10.3119 0.384382 9.68806 0.384382 9.54477 0.846855L7.63027 7.02616C7.56619 7.23298 7.38241 7.37301 7.17504 7.37301H0.979572C0.515888 7.37301 0.323098 7.99527 0.698226 8.28109L5.71047 12.1001C5.87823 12.2279 5.94843 12.4545 5.88435 12.6613L3.96984 18.8406C3.82656 19.3031 4.33129 19.6877 4.70642 19.4019L9.71865 15.5828C9.88642 15.455 10.1136 15.455 10.2813 15.5828L15.2936 19.4019C15.6687 19.6877 16.1734 19.3031 16.0302 18.8406L14.1157 12.6613C14.0516 12.4545 14.1218 12.2279 14.2895 12.1001L19.3018 8.28109C19.6769 7.99527 19.4841 7.37301 19.0204 7.37301H12.825C12.6176 7.37301 12.4338 7.23298 12.3697 7.02616L10.4552 0.846855Z';

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatDate(iso) {
    var date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  function starSvg(filled) {
    return (
      '<svg class="rating-star-svg okeReviews-star ' +
      (filled ? 'okeReviews-star--filled' : 'okeReviews-star--empty') +
      '" viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">' +
      '<path d="' + STAR_PATH + '" fill-rule="evenodd" fill="currentColor"></path>' +
      '</svg>'
    );
  }

  function formatRating(rating) {
    var value = Number(rating) || 0;
    // Whole numbers show without a decimal (e.g. "5"), otherwise one decimal (e.g. "4.9").
    return value % 1 === 0 ? String(value) : value.toFixed(1);
  }

  function renderRating(rating, title) {
    return (
      '<div class="flex flex-col">' +
      '<div class="okeReviews-review-rating" role="img" aria-label="' +
      escapeHtml(rating) + ' out of 5 stars">' +
      starSvg(true) +
      '<span class="okeReviews-review-rating-value">' + escapeHtml(formatRating(rating)) + '</span>' +
      '</div>' +
      (title ? '<div class="okeReviews-review-title">' + escapeHtml(title) + '</div>' : '') +
      '</div>'
    );
  }

  function renderMedia(media, extraClass) {
    if (!Array.isArray(media) || !media.length) return '';
    // Only render the first piece of media (image/video) per review.
    var item = media[0];
    if (!item) return '';
    var modifierClass = extraClass ? ' ' + extraClass : '';

    var inner;
    if (item.type === 'video') {
      var poster = item.largeUrl || '';
      var src = item.fullSizeUrl || item.largeUrl || '';
      if (!src) return '';
      inner =
        '<video class="okeReviews-review-media-video" src="' + escapeHtml(src) + '"' +
        (poster ? ' poster="' + escapeHtml(poster) + '"' : '') +
        ' controls playsinline preload="none"></video>';
    } else {
      var thumb = item.largeUrl || item.fullSizeUrl;
      if (!thumb) return '';
      // Image opens the review sidebar via the theme's drawer convention
      // (see sidebars.js — `data-open-drawer` accepts a `#id` or handle).
      inner =
        '<img class="okeReviews-review-media-image" src="' + escapeHtml(thumb) + '"' +
        ' alt="' + escapeHtml(item.alt || 'Customer review image') + '" loading="lazy"' +
        ' role="button" tabindex="0" data-oke-open-review data-open-drawer="#product-review" />';
    }
    return '<div class="okeReviews-review-media' + modifierClass + '">' + inner + '</div>';
  }

  function getAgeRange(reviewer) {
    var attributes = (reviewer && reviewer.attributes) || [];
    for (var i = 0; i < attributes.length; i++) {
      if (attributes[i] && attributes[i].title === 'Age Range') {
        var value = attributes[i].value;
        return Array.isArray(value) ? value.join(', ') : value;
      }
    }
    return '';
  }

  // --- Review filtering --------------------------------------------------------
  // Okendo's Storefront API exposes no server-side review facets, so filter
  // options are derived from the fetched reviews and applied client-side. The UI
  // mirrors the collection sidebar (chips + clear-all + result count).
  var CARET_SVG =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M7.29836 7.05586L10 11L12.7791 7.00829L14 6.9994L10 13.0006L6 7.03704L7.29836 7.05586Z" fill="currentColor"></path>' +
    '</svg>';

  var CLOSE_SVG =
    '<svg width="6" height="8" viewBox="0 0 6 8" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M4.7795 8H5.98503L1.25966 0H0L4.7795 8Z" fill="currentColor"></path>' +
    '<path d="M1.2205 8H0.0149668L4.74034 0H6L1.2205 8Z" fill="currentColor"></path>' +
    '</svg>';

  var SORT_OPTIONS = [
    { value: 'date desc', label: 'Most Recent' },
    { value: 'date asc', label: 'Oldest' },
    { value: 'rating desc', label: 'Highest Rating' },
    { value: 'rating asc', label: 'Lowest Rating' },
  ];
  var DEFAULT_SORT = 'date desc';

  // Attribute groups with more distinct values than this are treated as
  // free-text (not useful as filters) and skipped.
  var MAX_ATTRIBUTE_VALUES = 12;

  function hasReviewMedia(review) {
    var media = review && review.media;
    if (!Array.isArray(media)) return false;
    for (var i = 0; i < media.length; i++) {
      var item = media[i];
      if (item && (item.type === 'video' || item.largeUrl || item.fullSizeUrl)) return true;
    }
    return false;
  }

  function toValueArray(value) {
    if (Array.isArray(value)) return value;
    return value == null || value === '' ? [] : [value];
  }

  function getReviewAttributeValues(review, title) {
    var attributes = (review.reviewer && review.reviewer.attributes) || [];
    for (var i = 0; i < attributes.length; i++) {
      if (attributes[i] && attributes[i].title === title) {
        return toValueArray(attributes[i].value);
      }
    }
    return [];
  }

  function ratingLabel(star) {
    return star + (star === 1 ? ' Star' : ' Stars');
  }

  function renderReview(review) {
    var reviewer = review.reviewer || {};
    var ageRange = getAgeRange(reviewer);

    return (
      '<div class="okeReviews-reviews-review" data-oke-review-id="' +
      escapeHtml(review.reviewId) + '">' +
        '<div class="okeReviews-review-side">' +
          '<div class="okeReviews-review-reviewer">' +
            '<div class="okeReviews-review-reviewer-profile">' +
              '<div class="okeReviews-review-reviewer-profile-avatar"></div>' +
              '<div class="okeReviews-review-reviewer-profile-details">' +
                '<div class="okeReviews-review-reviewer-profile-name">' +
                  escapeHtml(reviewer.displayName || 'Anonymous') +
                '</div>' +
                (reviewer.isVerified
                  ? '<div class="okeReviews-review-reviewer-profile-status">Verified Buyer</div>'
                  : '') +
              '</div>' +
            '</div>' +
          '</div>' +
          (ageRange
            ? '<div class="okeReviews-review-attribute okeReviews-review-attribute--ageRange">' +
                '<span class="okeReviews-review-attribute-title">Age:</span> ' +
                '<span class="okeReviews-review-attribute-value">' + escapeHtml(ageRange) + '</span>' +
              '</div>'
            : '') +
          '<div class="okeReviews-review-date">' + escapeHtml(formatDate(review.dateCreated)) + '</div>' +
        '</div>' +
        '<div class="okeReviews-review-primary">' +
          '<div class="okeReviews-review-header">' +
            renderRating(review.rating, review.title) +
            // Duplicate media — shown only on mobile.
            renderMedia(review.media, 'desktop:hidden') +
          '</div>' +
          (review.body
            ? '<div class="okeReviews-review-body">' + escapeHtml(review.body) + '</div>'
            : '') +
        '</div>' +
        renderMedia(review.media, 'mobile:hidden') +
      '</div>'
    );
  }

  function initOkendoReviews(root) {
    var userId = root.getAttribute('data-oke-user-id');
    var productId = root.getAttribute('data-oke-product-id');
    var list = root.querySelector('[data-oke-reviews-list]');
    var loading = root.querySelector('[data-oke-reviews-loading]');
    var moreBtn = root.querySelector('[data-oke-reviews-more]');
    var summaryRating = root.querySelector('[data-oke-summary-rating]');
    var filtersBar = root.querySelector('.reviews__filters');

    // No product context (e.g. not a product page) — nothing to fetch.
    if (!userId || !productId || productId === 'shopify-') {
      if (loading) loading.textContent = 'No reviews available.';
      return;
    }

    var storeBase =
      API_ORIGIN +
      '/v1/stores/' + encodeURIComponent(userId) +
      '/products/' + encodeURIComponent(productId);
    // The product endpoint's `lastEvaluated` cursor is unreliable, so we fetch a
    // large batch once and reveal it client-side instead of paginating.
    var reviewsUrl =
      storeBase + '/reviews?limit=' + FETCH_LIMIT + '&orderBy=' + encodeURIComponent('date desc');
    var aggregateUrl = storeBase + '/review_aggregate';

    var allReviews = []; // every fetched review
    var currentReviews = []; // reviews after active filters + sort
    var shownCount = 0; // how many are currently rendered
    var filtersSidebar = document.querySelector('#product-review-filters');
    var activeSort = DEFAULT_SORT;

    var sliderEl = root.querySelector('#reviews__full-slider');
    var sliderWrapper = sliderEl ? sliderEl.querySelector('.swiper-wrapper') : null;
    var mediaSwiper = null;

    // The review sidebar mirrors the full media slider so every review's media is
    // browsable from inside the open sidebar. The sidebar is rendered outside
    // `root` (see product-review-sidebar.liquid), so it's queried from the document.
    var sidebarSliderEl = document.querySelector('#reviews__sidebar-slider');
    var sidebarSliderWrapper = sidebarSliderEl ? sidebarSliderEl.querySelector('.swiper-wrapper') : null;
    var sidebarListEl = sidebarSliderEl ? sidebarSliderEl.closest('.review-sidebar-list') : null;
    var sidebarSwiper = null;
    // Hide the sidebar media slider unless there are at least this many slides.
    var SIDEBAR_SLIDER_MIN = 3;

    function renderSlide(item, reviewId) {
      // Each slide opens the sidebar for its own review (see click handler below).
      var trigger =
        ' role="button" tabindex="0" data-oke-open-review data-open-drawer="#product-review"' +
        ' data-oke-review-id="' + escapeHtml(reviewId) + '"';
      var inner;
      if (item.type === 'video') {
        var src = item.fullSizeUrl || item.largeUrl || '';
        var poster = item.largeUrl || '';
        if (!src) return '';
        inner =
          '<video class="reviews__slide-video" src="' + escapeHtml(src) + '"' +
          (poster ? ' poster="' + escapeHtml(poster) + '"' : '') +
          ' muted playsinline preload="none"' + trigger + '></video>';
      } else {
        var thumb = item.largeUrl || item.fullSizeUrl;
        if (!thumb) return '';
        inner =
          '<img class="reviews__slide-image" src="' + escapeHtml(thumb) + '"' +
          ' alt="' + escapeHtml(item.alt || 'Customer review media') + '" loading="lazy"' + trigger + ' />';
      }
      return '<div class="swiper-slide">' + inner + '</div>';
    }

    // (Re)initialise a Swiper into `wrapper`, destroying any previous instance
    // first. Returns the new Swiper, or null when there's nothing to show / the
    // target is absent / Swiper hasn't loaded.
    function initSlider(el, wrapper, existing, slides, config) {
      if (existing && existing.destroy) existing.destroy(true, true);
      if (!el || !wrapper) return null;
      if (!slides.length) {
        wrapper.innerHTML = '';
        return null;
      }
      wrapper.innerHTML = slides.join('');
      if (typeof Swiper === 'undefined') return null;
      return new Swiper(el, config);
    }

    // Collect every image/video from all visible reviews and render them as slides
    // into both #reviews__full-slider and the in-sidebar #reviews__sidebar-slider.
    // Rebuilt whenever the visible reviews change.
    function buildMediaSlider() {
      var slides = [];
      for (var i = 0; i < currentReviews.length; i++) {
        var review = currentReviews[i];
        var media = review.media;
        if (!Array.isArray(media)) continue;
        for (var m = 0; m < media.length; m++) {
          var item = media[m];
          if (item && (item.type === 'video' || item.largeUrl || item.fullSizeUrl)) {
            slides.push(renderSlide(item, review.reviewId));
          }
        }
      }

      mediaSwiper = initSlider(sliderEl, sliderWrapper, mediaSwiper, slides, {
        slidesPerView: 4.2,
        spaceBetween: 1,
        watchOverflow: true,
        mousewheel: { forceToAxis: true, releaseOnEdges: false },
        navigation: {
          prevEl: sliderEl && sliderEl.querySelector('.slide-prev'),
          nextEl: sliderEl && sliderEl.querySelector('.slide-next'),
        },
        breakpoints: {
          1024: {
            slidesPerView: 6
          }
        }
      });

      // Only show the sidebar slider when there are enough images to be worth it.
      var sidebarSlides = slides.length >= SIDEBAR_SLIDER_MIN ? slides : [];
      if (sidebarListEl) sidebarListEl.hidden = !sidebarSlides.length;
      sidebarSwiper = initSlider(sidebarSliderEl, sidebarSliderWrapper, sidebarSwiper, sidebarSlides, {
        slidesPerView: 4.2,
        spaceBetween: 1,
        watchOverflow: true,
        mousewheel: { forceToAxis: true, releaseOnEdges: false },
        breakpoints: {1024: {slidesPerView: 2.1}},
        navigation: {
          prevEl: sidebarSliderEl && sidebarSliderEl.querySelector('.slide-prev'),
          nextEl: sidebarSliderEl && sidebarSliderEl.querySelector('.slide-next'),
        }
      });
    }

    // Swap the clicked review's content into the sidebar. The content (rating,
    // title, body, and its single image) is written into `.reivew-sidebar-content`
    // and changes per review; the media slider in `.review-sidebar-list` is a
    // separate, additional section showing every review's media (buildMediaSlider).
    // Clicking any slide (full slider or sidebar slider) refreshes the content.
    // Runs before sidebars.js opens the sidebar (this listener sits deeper in the
    // DOM than its document-level open handler).
    function populateSidebar(review) {
      var sidebar = document.querySelector('#product-review');
      if (!sidebar) return;

      var nameEl = sidebar.querySelector('#product-review-name');
      var contentEl = sidebar.querySelector('.reivew-sidebar-content');
      var reviewer = review.reviewer || {};

      if (nameEl) nameEl.textContent = reviewer.displayName || 'Anonymous';
      if (!contentEl) return;

      var badge = reviewer.isVerified ? '<span class="badge">Verified Buyer</span>' : '';
      // Build the media markup with the verified badge nested inside it.
      var mediaHtml = renderMedia(review.media);
      if (mediaHtml && badge) {
        mediaHtml = mediaHtml.replace('</div>', badge + '</div>');
      }

      contentEl.innerHTML =
        '<div class="okeReviews-review-rating" role="img" aria-label="' +
        escapeHtml(review.rating) + ' out of 5 stars">' +
        starSvg(true) +
        '<span class="okeReviews-review-rating-value">' + escapeHtml(formatRating(review.rating)) + '</span>' +
        '</div>' +
        (review.title
          ? '<div class="okeReviews-review-title">' + escapeHtml(review.title) + '</div>'
          : '') +
        (review.body
          ? '<div class="okeReviews-review-body">' + escapeHtml(review.body) + '</div>'
          : '') +
        mediaHtml;
    }

    function fetchSummary() {
      if (!summaryRating) return;
      fetch(aggregateUrl, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Okendo aggregate failed: ' + response.status);
          return response.json();
        })
        .then(function (data) {
          var aggregate = (data && data.reviewAggregate) || {};
          var count = aggregate.ratingAndReviewCount || 0;
          if (!count) return;

          var average = (aggregate.ratingAndReviewValuesTotal || 0) / count;
          summaryRating.innerHTML =
            '<span class="okeReviews-summary-rating-value">' + escapeHtml(formatRating(average)) + '</span>' +
            starSvg(true) +
            '<span class="okeReviews-summary-count text-secondary">(' + escapeHtml(count) +
            (count === 1 ? ' Review' : ' Reviews') + ')</span>';
          summaryRating.hidden = false;
        })
        .catch(function (error) {
          console.error('[Okendo] Unable to load review summary', error);
        });
    }

    // Reveal the next batch of already-fetched reviews.
    function showMore(count) {
      var next = currentReviews.slice(shownCount, shownCount + count);
      if (next.length) {
        list.insertAdjacentHTML('beforeend', next.map(renderReview).join(''));
        shownCount += next.length;
      }
      // Inline style (not the `hidden` attr) so the `.btn` display rule can't override it.
      if (shownCount >= currentReviews.length) {
        moreBtn.style.display = 'none';
      } else {
        moreBtn.hidden = false;
        moreBtn.style.display = '';
      }
      buildMediaSlider();
    }

    function fetchReviews() {
      return fetch(reviewsUrl, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Okendo request failed: ' + response.status);
          return response.json();
        })
        .then(function (data) {
          if (loading) {
            loading.remove();
            loading = null;
          }

          allReviews = (data && data.reviews) || [];
          if (!allReviews.length) {
            list.insertAdjacentHTML(
              'beforeend',
              '<div class="okeReviews-reviews-empty">No reviews yet.</div>'
            );
            moreBtn.style.display = 'none';
            if (filtersBar) filtersBar.hidden = true;
            return;
          }

          currentReviews = allReviews.slice();
          buildFilterUI();
          applyFilters();
        })
        .catch(function (error) {
          console.error('[Okendo] Unable to load reviews', error);
          if (loading) loading.textContent = 'Reviews are currently unavailable.';
          moreBtn.style.display = 'none';
        });
    }

    // --- Filter UI + state ---------------------------------------------------

    function groupHtml(title, optionsHtml, open) {
      return (
        '<details class="facets__panel"' + (open ? ' open' : '') + '>' +
          '<summary class="facets__summary">' +
            '<span class="facets__label">' + escapeHtml(title) + '</span>' +
            '<span class="svg-wrapper icon-caret icon-animated">' + CARET_SVG + '</span>' +
          '</summary>' +
          '<div class="facets__inputs facets__panel-content details-content">' +
            '<div class="facets__inputs-list list-unstyled">' + optionsHtml + '</div>' +
          '</div>' +
        '</details>'
      );
    }

    function optionHtml(type, key, value, label) {
      var id = ('oke-filter-' + type + '-' + (key ? key + '-' : '') + value).replace(/[^a-zA-Z0-9_-]+/g, '-');
      return (
        '<label class="checkbox-block-option" for="' + id + '">' +
          '<input type="checkbox" id="' + id + '" data-oke-filter-input data-filter-type="' + type + '"' +
          (key ? ' data-filter-key="' + escapeHtml(key) + '"' : '') +
          ' value="' + escapeHtml(value) + '">' +
          escapeHtml(label) +
        '</label>'
      );
    }

    function sortLabel(value) {
      for (var i = 0; i < SORT_OPTIONS.length; i++) {
        if (SORT_OPTIONS[i].value === value) return SORT_OPTIONS[i].label;
      }
      return '';
    }

    function buildSortGroupHtml() {
      var options = SORT_OPTIONS.map(function (opt) {
        var id = 'oke-sort-' + opt.value.replace(/\s+/g, '-');
        return (
          '<label class="radio-block-option" for="' + id + '">' +
            '<input type="radio" name="oke-review-sort" id="' + id + '" value="' + escapeHtml(opt.value) + '"' +
            ' data-option-name="' + escapeHtml(opt.label) + '"' +
            (opt.value === DEFAULT_SORT ? ' checked' : '') + '>' +
            escapeHtml(opt.label) +
          '</label>'
        );
      }).join('');

      return (
        '<details class="facets__panel" open>' +
          '<summary class="facets__summary">' +
            '<span class="facets__label">Sort</span>' +
            '<span class="review-filters-sidebar__sort-status flex items-center">' +
              '<span class="facets__status text-f0 text-secondary" data-review-sort-status>' +
                escapeHtml(sortLabel(DEFAULT_SORT)) +
              '</span>' +
              '<span class="svg-wrapper icon-caret icon-animated">' + CARET_SVG + '</span>' +
            '</span>' +
          '</summary>' +
          '<div class="facets__inputs facets__panel-content details-content">' +
            '<div class="facets__inputs-list list-unstyled">' + options + '</div>' +
          '</div>' +
        '</details>'
      );
    }

    function buildFilterGroupsHtml() {
      var attrValues = {}; // title -> { value: true }

      for (var i = 0; i < allReviews.length; i++) {
        var review = allReviews[i];
        var attributes = (review.reviewer && review.reviewer.attributes) || [];
        for (var a = 0; a < attributes.length; a++) {
          var attr = attributes[a];
          if (!attr || !attr.title) continue;
          var values = toValueArray(attr.value);
          for (var v = 0; v < values.length; v++) {
            var bucket = attrValues[attr.title] || (attrValues[attr.title] = {});
            bucket[values[v]] = true;
          }
        }
      }

      var groups = [];

      Object.keys(attrValues).forEach(function (title) {
        var values = Object.keys(attrValues[title]);
        if (!values.length || values.length > MAX_ATTRIBUTE_VALUES) return;
        values.sort();
        var opts = values
          .map(function (value) {
            return optionHtml('attr', title, value, value);
          })
          .join('');
        groups.push(groupHtml(title, opts, true));
      });

      return groups.join('');
    }

    function readActiveFilters() {
      var filters = { ratings: [], media: false, verified: false, attrs: {} };
      if (!filtersSidebar) return filters;

      var inputs = filtersSidebar.querySelectorAll('[data-oke-filter-input]:checked');
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        var type = input.getAttribute('data-filter-type');
        if (type === 'rating') {
          filters.ratings.push(Number(input.value));
        } else if (type === 'media') {
          filters.media = true;
        } else if (type === 'verified') {
          filters.verified = true;
        } else if (type === 'attr') {
          var key = input.getAttribute('data-filter-key');
          (filters.attrs[key] = filters.attrs[key] || []).push(input.value);
        }
      }
      return filters;
    }

    function reviewMatchesFilters(review, filters) {
      if (filters.ratings.length && filters.ratings.indexOf(Math.round(review.rating)) === -1) return false;
      if (filters.media && !hasReviewMedia(review)) return false;
      if (filters.verified && !(review.reviewer && review.reviewer.isVerified)) return false;

      for (var title in filters.attrs) {
        if (!Object.prototype.hasOwnProperty.call(filters.attrs, title)) continue;
        var selected = filters.attrs[title];
        var values = getReviewAttributeValues(review, title);
        var matched = false;
        for (var i = 0; i < selected.length; i++) {
          if (values.indexOf(selected[i]) !== -1) {
            matched = true;
            break;
          }
        }
        if (!matched) return false;
      }
      return true;
    }

    function sortReviews(reviews) {
      return reviews.slice().sort(function (a, b) {
        switch (activeSort) {
          case 'rating desc':
            return (b.rating || 0) - (a.rating || 0);
          case 'rating asc':
            return (a.rating || 0) - (b.rating || 0);
          case 'date asc':
            return new Date(a.dateCreated) - new Date(b.dateCreated);
          default:
            return new Date(b.dateCreated) - new Date(a.dateCreated);
        }
      });
    }

    function activeFilterCount(filters) {
      var count = filters.ratings.length + (filters.media ? 1 : 0) + (filters.verified ? 1 : 0);
      Object.keys(filters.attrs).forEach(function (title) {
        count += filters.attrs[title].length;
      });
      return count;
    }

    function findFilterInput(pill) {
      if (!filtersSidebar) return null;
      var inputs = filtersSidebar.querySelectorAll('[data-oke-filter-input][data-filter-type="' + pill.type + '"]');
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        if (pill.type === 'attr' && input.getAttribute('data-filter-key') !== pill.key) continue;
        if ((pill.type === 'rating' || pill.type === 'attr') && input.value !== pill.value) continue;
        return input;
      }
      return null;
    }

    function createPill(pill) {
      var item = document.createElement('div');
      item.className = 'solution-selected-list__item';

      var label = document.createElement('span');
      label.textContent = pill.label;

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'solution-selected-list__remove btn btn-semantic';
      removeBtn.setAttribute('aria-label', 'Remove ' + pill.label);
      removeBtn.innerHTML = '<span class="svg-wrapper svg-wrapper--smaller" style="width: 4px; align-self: center; margin-top: -2px">' + CLOSE_SVG + '</span>';
      removeBtn.addEventListener('click', function () {
        var input = findFilterInput(pill);
        if (input) input.checked = false;
        applyFilters();
      });

      item.append(label, removeBtn);
      return item;
    }

    function updateSelectedPills(filters) {
      if (!filtersSidebar) return;
      var pillsRoot = filtersSidebar.querySelector('[data-review-filter-pills]');
      if (!pillsRoot) return;

      var pills = [];
      filters.ratings.forEach(function (rating) {
        pills.push({ type: 'rating', value: String(rating), label: ratingLabel(rating) });
      });
      if (filters.media) pills.push({ type: 'media', value: 'true', label: 'Photos & Videos' });
      if (filters.verified) pills.push({ type: 'verified', value: 'true', label: 'Verified Buyers' });
      Object.keys(filters.attrs).forEach(function (title) {
        filters.attrs[title].forEach(function (value) {
          pills.push({ type: 'attr', key: title, value: value, label: value });
        });
      });

      pillsRoot.replaceChildren();
      for (var i = 0; i < pills.length; i++) pillsRoot.appendChild(createPill(pills[i]));
    }

    function updateResultsCount() {
      if (!filtersSidebar) return;
      var resultsBtn = filtersSidebar.querySelector('[data-review-results]');
      if (!resultsBtn) return;
      var count = currentReviews.length;
      resultsBtn.textContent = 'Show ' + count + (count === 1 ? ' Results' : ' Reviews');
    }

    function updateFilterControls(filters) {
      var count = activeFilterCount(filters);

      var bubble = root.querySelector('[data-oke-filter-count]');
      if (bubble) {
        bubble.textContent = count ? String(count) : '';
        bubble.hidden = count === 0;
      }

      var clearBtn = filtersSidebar && filtersSidebar.querySelector('[data-review-clear-all]');
      if (clearBtn) clearBtn.hidden = !(count > 0 || activeSort !== DEFAULT_SORT);
    }

    function applyFilters() {
      var filters = readActiveFilters();
      var filtered = allReviews.filter(function (review) {
        return reviewMatchesFilters(review, filters);
      });
      currentReviews = sortReviews(filtered);

      list.innerHTML = '';
      shownCount = 0;

      if (!currentReviews.length) {
        list.insertAdjacentHTML(
          'beforeend',
          '<div class="okeReviews-reviews-empty">No reviews match these filters.</div>'
        );
        moreBtn.style.display = 'none';
        buildMediaSlider();
      } else {
        showMore(INITIAL_COUNT);
      }

      updateSelectedPills(filters);
      updateResultsCount();
      updateFilterControls(filters);
    }

    function buildFilterUI() {
      if (!filtersSidebar || filtersSidebar.dataset.okeFiltersInit === 'true') return;
      filtersSidebar.dataset.okeFiltersInit = 'true';

      var sortHost = filtersSidebar.querySelector('[data-review-sort]');
      var listHost = filtersSidebar.querySelector('[data-review-filters-list]');
      if (sortHost) sortHost.innerHTML = buildSortGroupHtml();
      if (listHost) listHost.innerHTML = buildFilterGroupsHtml();

      filtersSidebar.addEventListener('change', function (event) {
        var target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name === 'oke-review-sort') {
          activeSort = target.value;
          var sortStatus = filtersSidebar.querySelector('[data-review-sort-status]');
          if (sortStatus) sortStatus.textContent = target.getAttribute('data-option-name') || '';
          applyFilters();
        } else if (target.hasAttribute('data-oke-filter-input')) {
          applyFilters();
        }
      });

      var clearBtn = filtersSidebar.querySelector('[data-review-clear-all]');
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          var checked = filtersSidebar.querySelectorAll('[data-oke-filter-input]:checked');
          for (var i = 0; i < checked.length; i++) checked[i].checked = false;

          var defaultSort = filtersSidebar.querySelector('input[name="oke-review-sort"][value="' + DEFAULT_SORT + '"]');
          if (defaultSort) defaultSort.checked = true;
          activeSort = DEFAULT_SORT;

          var sortStatus = filtersSidebar.querySelector('[data-review-sort-status]');
          if (sortStatus) sortStatus.textContent = sortLabel(DEFAULT_SORT);

          applyFilters();
        });
      }
    }

    moreBtn.addEventListener('click', function () {
      showMore(MORE_COUNT);
    });

    // Delegated on document so it works for async-rendered images. Clicking a
    // review image refills the single shared sidebar before sidebars.js opens
    // it (capture phase guarantees we run first).
    document.addEventListener(
      'click',
      function (e) {
        var trigger = e.target instanceof Element && e.target.closest('[data-oke-open-review]');
        if (!trigger) return;
        // Slider slides carry the id directly; review cards inherit it from the wrapper.
        var id = trigger.getAttribute('data-oke-review-id');
        if (!id) {
          var reviewEl = trigger.closest('.okeReviews-reviews-review');
          if (reviewEl) id = reviewEl.getAttribute('data-oke-review-id');
        }
        if (!id) return;
        var review = allReviews.filter(function (r) {
          return r.reviewId === id;
        })[0];
        if (review) populateSidebar(review);
      },
      true
    );

    fetchSummary();
    fetchReviews();
  }

  function init() {
    var roots = document.querySelectorAll('[data-oke-reviews]');
    for (var i = 0; i < roots.length; i++) {
      initOkendoReviews(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
